import { yupResolver } from '@hookform/resolvers/yup';
import { FunctionComponent } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import * as yup from 'yup';

import {
  Button,
  Card,
  CardTitle,
  Form,
  FormFeedback,
  FormGroup,
  SelectControl,
} from '../../components';
import { ReactComponent as TeacherIcon } from '../../assets/icons/teacher.svg';
import { ReactComponent as GoogleLogo } from '../../assets/logos/google-g-icon.svg';
import useGoogleSignIn from '../../behaviors/api-hooks/use-google-sign-in/use-google-sign-in';
import useNavigateWithSearchParams from '../../behaviors/use-navigate-with-search-params/use-navigate-with-search-params';
import useSessionCookies from '../../behaviors/use-session-cookies/use-session-cookies';
import { usePlatform } from '../../providers/PlatformProvider/PlatformProvider';
import { useSettings } from '../../providers/SettingsProvider/SettingsProvider';
import { fetcher, logError } from '../../utils/api/api';
import handleError from '../../utils/error-handler/error-handler';
import { ERROR_UNKNOWN } from '../../utils/error-messages';
import { BaseErrorResource, UserContext } from '../../utils/shape';
import CheckboxFields from './CheckboxFields';
import createAccountErrorMap from './CreateAccount.error-codes';
import EmailFields from './EmailFields';
import { InputField } from './InputField';
import PasswordFields from './PasswordFields';
import useClasses from '../../behaviors/use-classes/use-classes';
import Container from '../../components/Container/Container';
import { notify } from '../../components/Notifier';
import { useDpState } from '../../providers/DpStateProvider/DpStateProvider';
import { useWindowQuery } from '../../providers/ResponsivityProvider/ResponsivityProvider';

import './CreateAccount.scss';

export const baseClass = 'customer-portal-create-account';

export interface RegisterFormData extends Record<string, string | boolean | TitleDropdownOption> {
  title: string;
  firstName: string;
  lastName: string;
  email: string;
  confirmEmail: string;
  password: string;
  confirmPassword: string;
  confirmPrivacyPolicy: boolean;
  confirmTermsOfUse: boolean;
}

interface RegisterRequestParams {
  username?: string;
  ['g-recaptcha-response']?: string;
  basicProfile: {
    [key: string]: string;
    email: string;
    firstName: string;
    lastName: string;
    title: string;
  };
  credentials: {
    [key: string]: string;
    password: string;
  };
}

interface TitleDropdownOption {
  value: string;
  label: string;
}

const schema = yup.object().shape({
  title: yup.string().required('Title is required'),
  firstName: yup
    .string()
    .required(`First name is required`)
    .matches(
      new RegExp(/^[a-zA-Z .'-]+$/),
      'Not a valid first name', // make this better
    ),
  lastName: yup
    .string()
    .required(`Last name is required`)
    .matches(
      new RegExp(/^[a-zA-Z .'-]+$/),
      'Not a valid last name', // make this better
    ),
  email: yup.string().email('Please provide a valid email').required('Email is required'),
  confirmEmail: yup
    .string()
    .required('Email confirmation is required')
    .oneOf([yup.ref('email')], 'Your emails do not match'),
  password: yup
    .string()
    .min(7, 'Your password must be at least 7 characters long.')
    .matches(
      new RegExp(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/),
      `Your password must contain at least one capital letter, one lower case letter, and one number.`,
    )
    .required(`Password is required`),
  confirmPassword: yup
    .string()
    .required('Password confirmation is required')
    .oneOf([yup.ref('password')], 'Your passwords do not match'),
  confirmPrivacyPolicy: yup
    .bool()
    .isTrue('Please confirm you have read and agree to our Privacy Policy'),
  confirmTermsOfUse: yup
    .bool()
    .isTrue('Please confirm you have read and agree to our Terms of Use'),
});

const titleDropdownOptions: TitleDropdownOption[] = [
  { value: 'MR', label: 'MR' },
  { value: 'MRS', label: 'MRS' },
  { value: 'MISS', label: 'MISS' },
  { value: 'MS', label: 'MS' },
  { value: 'DR', label: 'DOCTOR' },
  { value: 'BR', label: 'BROTHER' },
  { value: 'CAPT', label: 'CAPTAIN' },
  { value: 'FR', label: 'FATHER' },
  { value: 'LT', label: 'LIEUTENANT' },
  { value: 'MSG', label: 'MONSIGNOR' },
  { value: 'PR', label: 'PASTOR' },
  { value: 'PROF', label: 'PROFESSOR' },
  { value: 'RAB', label: 'RABBI' },
  { value: 'REV', label: 'REVEREND' },
  { value: 'SGT', label: 'SERGEANT' },
  { value: 'SR', label: 'SISTER' },
  { value: 'NA', label: 'Not Applicable' },
];

export const CreateAccount: FunctionComponent = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { navigateWithSearchParams } = useNavigateWithSearchParams();

  const { dispatch } = useDpState();
  const { initializeSession, track } = usePlatform();
  const { initializeSessionCookie } = useSessionCookies();
  const { googleLoginEnabled, googleRecaptchaEnabled } = useSettings();

  const { responsiveClassName } = useWindowQuery({ className: baseClass });
  const classes = useClasses({ baseClass }, [responsiveClassName]);

  const signInWithGoogle = useGoogleSignIn({ googleLoginEnabled });

  const methods = useForm<RegisterFormData>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: {
      title: '',
      confirmTermsOfUse: false,
      confirmPrivacyPolicy: false,
      email: '',
      password: '',
      confirmEmail: '',
      confirmPassword: '',
    },
  });

  const {
    register,
    control,
    handleSubmit,
    setValue,
    trigger,
    setError,
    formState: { isValid, errors },
  } = methods;

  const onSubmit = async ({
    email,
    firstName,
    lastName,
    password,
    title,
    confirmPassword,
  }: RegisterFormData) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const recaptcha = window.grecaptcha;
    const params: RegisterRequestParams = {
      basicProfile: {
        email,
        firstName,
        lastName,
        title,
      },
      credentials: {
        password,
      },
    };

    if (password !== confirmPassword) {
      setError('confirmPassword', { message: 'Your passwords do not match.' });
      return;
    }

    const submit = async (token?: string) => {
      try {
        if (token) {
          params['g-recaptcha-response'] = token;
          params.username = email;
        }

        const data = await fetcher<UserContext>('register', {
          method: 'POST',
          body: JSON.stringify(params),
        });

        if (data.sessionContext) {
          dispatch({ type: 'user', value: data });
          initializeSessionCookie(data);
          initializeSession();

          const appCode = searchParams.get('app');
          const acode = searchParams.get('acode');

          if (appCode && !acode) {
            searchParams.append('status', 'get-alternate-url');
            setSearchParams(searchParams);
          }

          track('registration-completed', {
            registrationMethod: 'Manual',
            userSdmId: data.staff?.id,
          });

          const paramsArray = Array.from(searchParams.entries());

          if (paramsArray.length) {
            navigateWithSearchParams(
              '/teacher',
              paramsArray.reduce<Record<string, string>>(
                (map, entry) => ({
                  ...map,
                  [entry[0]]: entry[1],
                }),
                {},
              ),
            );

            return;
          }

          navigateWithSearchParams('/teacher');
          return;
        }

        const { code, message } = data as BaseErrorResource;
        track('registration-error', { errorMessage: code || message });
        handleError(createAccountErrorMap, data as BaseErrorResource);
      } catch (e) {
        logError(e as string);
        notify.warning(ERROR_UNKNOWN);
      }
    };

    if (googleRecaptchaEnabled) {
      recaptcha.ready(async () => {
        return recaptcha
          .execute('6LcWqkgaAAAAAL8IQnmcS8z65iJYy5FYvXgcfInM', {
            action: 'submit',
          })
          .then(submit);
      });
    } else {
      await submit();
    }
  };

  return (
    <Container className={classes}>
      <Card className={`${baseClass}__card`} tag="h1">
        <div className={`${baseClass}__header`}>
          <TeacherIcon className={`${baseClass}__teacher-icon`} />
          <CardTitle className={`${baseClass}__title`}>
            <h2>Educator and Librarian Registration</h2>
          </CardTitle>
        </div>
        <div className={`${baseClass}__sign-in`}>
          <p>
            Already have a Scholastic.com account?{' '}
            <Button
              className="link"
              color="default"
              link
              onClick={() => navigateWithSearchParams('/sign-in/staff')}
              data-testid="staffSignInLink"
            >
              Sign in
            </Button>
          </p>
          {googleLoginEnabled ? (
            <>
              <Button
                color="light"
                className={`${baseClass}__gclass-button`}
                onClick={signInWithGoogle}
              >
                <GoogleLogo title="Sign in with Google Classroom" />{' '}
                <p className={`${baseClass}__button-text`}>SIGN UP WITH GOOGLE CLASSROOM</p>
              </Button>
              <p>Or register a new account:</p>
            </>
          ) : undefined}
        </div>
        <FormProvider {...methods}>
          <Form
            className={`${baseClass}__form`}
            onSubmit={handleSubmit(onSubmit)}
            data-testid="registerForm"
          >
            <FormGroup className={`${baseClass}__form-group`}>
              <span className={`${baseClass}__label`}>Title</span>
              <Controller
                control={control}
                name="title"
                render={() => (
                  <SelectControl
                    aria-label="Title"
                    className={`${baseClass}__control`}
                    options={titleDropdownOptions}
                    placeholder="Select a Title"
                    onChange={(value: TitleDropdownOption) => {
                      setValue('title', (value as TitleDropdownOption).value);
                      trigger('title');
                    }}
                  />
                )}
              />
              <FormFeedback>{errors.title?.message}</FormFeedback>
            </FormGroup>
            <InputField name="First Name" id="firstName" register={register} errors={errors} />
            <InputField name="Last Name" id="lastName" register={register} errors={errors} />
            <EmailFields register={register} errors={errors} setError={setError} />
            <PasswordFields register={register} errors={errors} setError={setError} />
            <CheckboxFields control={control} errors={errors} trigger={trigger} />
            <div className={`${baseClass}__button-container`}>
              <Button
                disabled={!isValid || Object.keys(errors).length > 0}
                type="submit"
                aria-label="register"
                color="danger"
                data-testid="registerButton"
              >
                Register
              </Button>
              <div className={`${baseClass}__spacer`} />
              <Button
                link
                color="primary"
                onClick={() => navigateWithSearchParams('/sign-in/staff')}
                data-testid="cancelButton"
              >
                Cancel
              </Button>
            </div>
          </Form>
        </FormProvider>
      </Card>
      <div className={`${baseClass}__footer`}>
        Need help? Visit our{' '}
        <a href="https://help.digital.scholastic.com/hc/en-us" target="_blank" rel="noreferrer">
          Help and Resources Page
        </a>{' '}
        or contact us at 1-866-826-8834.
      </div>
    </Container>
  );
};

export default CreateAccount;
